import { Suspense, useState } from "react";
import { EventInsuranceType, EventClaim, VehicleInsuranceType, VehicleClaimProcessStage } from "../../../types/eventClaim.types";
import { Modal } from "flowbite-react";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import { Formik, Form } from 'formik'; // Importe estes componentes
import * as Yup from 'yup'; // Opcional para validação
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../common/components/form/InputFormik";
import TextareaFormik from "../../renters/components/forms/TextareaFormik";
import { DocumentEventClaimRow } from "./DocumentRowEventClaim";
import { createEventCliam, updateEventClaim } from "../../../common/services/eventCliam.api";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import { maskLicensePlate, maskPhone } from "../../../common/helpers/mask.helpers";
import Loading from "@/common/components/ui/loading/Loading";

export type Props = {
  eventClaim?: EventClaim;
  onClose: () => void;
  onSave: (insuranceClaim: EventClaim) => void;
};

// Esquema de validação (opcional)
const InsuranceClaimSchema = Yup.object().shape({
  eventType: Yup.string().required('Tipo do evento é obrigatório'),
  eventDate: Yup.date().required('Data do evento é obrigatória'),
  licensePlate: Yup.string().required('Placa é obrigatória'),
  vehicleType: Yup.string().required('Tipo de veículo é obrigatório'),
  whatsapp: Yup.string().required('Telefone de WhatsApp é obrigatório'),
  associateName: Yup.string().required('Nome do associado é obrigatório'),
});

export function ModalEventClaimEdit(props: Props) {
  const [, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('resumo');
  const { notifyError } = useToastContext();

  const initialValues = props.eventClaim || {
    _id: undefined,
    eventType: '',
    eventDate: '',
    location: '',
    state: '',
    city: '',
    licensePlate: '',
    associateName: '',
    vehicleType: '',
    phone: '',
    whatsapp: '',
    email: '',
    reportText: '',
    documents: [],
    associatedTrigger: false,
    thirdPartiesInvolved: false,
    thirdPartiesCount: 0,
    thirdParties: [],
    status: VehicleClaimProcessStage.NOTICE,
  };

  // Função para lidar com o envio do formulário
  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __v, ...valuesWithout__v } = values;
      const cleanedValues = {
        ...valuesWithout__v,
        documents: values.documents?.map(doc => ({
          type: doc.type,
          content: doc.content,
          verified: doc.verified
        }))
      };

      let response;

      if (cleanedValues._id && cleanedValues._id.trim() !== "") {
        response = await updateEventClaim(cleanedValues);
      } else {
        response = await createEventCliam(cleanedValues);
      }

      if (!response.ok)
        return notifyError(response.message || "Erro ao salvar o sinistro. Por favor, tente novamente.");

      props.onSave(response.data);

      props.onClose();
    } catch (error) {
      console.error("Erro ao salvar sinistro:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Suspense fallback={<Loading />} >

      <Modal
        className='relative text-black'
        show={true}
        onClose={() => props.onClose()}
        size="7x"
        theme={{
          content: {
            base: "relative h-full w-full p-4 md:h-auto",
            inner: "relative rounded-lg bg-white shadow dark:bg-gray-700 max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col"
          }
        }}
      >
        <Modal.Header className='text-black'>
          <h2 className='text-black'>
            {initialValues._id ?
              `Evento: ${initialValues.associateName ?
                initialValues.associateName.split(' ').map(name =>
                  name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
                ).join(' ') : 'Desconhecido'
              }` : "Novo Evento"}
          </h2>
        </Modal.Header>
        <Modal.Body className="min-h-[509px]">
          {/* Envolva todo o conteúdo do formulário com Formik */}
          <Formik
            initialValues={initialValues}
            validationSchema={InsuranceClaimSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form>
                <div className="space-y-4 border rounded-lg p-3">

                  {/* Tab Navigation */}
                  <div className="flex mb-4 gap-3">
                    <button
                      type="button"
                      className={`px-4 py-2 ${activeTab === 'resumo'
                        ? 'bg-[#E44131] text-white rounded-t'
                        : 'text-gray-500'}`}
                      onClick={() => setActiveTab('resumo')}
                    >
                      Resumo
                    </button>
                    {(initialValues._id && initialValues._id !== '') && (
                      <button
                        type="button"
                        className={`px-4 py-2 ${activeTab === 'documentacao'
                          ? 'bg-[#E44131] text-white rounded-t'
                          : 'text-gray-500'}`}
                        onClick={() => setActiveTab('documentacao')}
                      >
                        Documentação
                      </button>
                    )}
                  </div>

                  {/* Tab Content */}
                  {activeTab === 'resumo' && (
                    <div className="resumo">
                      <div className="flex flex-col md:flex-row flex-wrap">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <ComboBoxFormik
                            label="Tipo do evento:"
                            name="eventType"
                            options={[
                              { value: "", label: "-" },
                              { value: EventInsuranceType.REVIEW, label: "REVISÃO" },
                              { value: EventInsuranceType.COLLISION, label: "COLISÃO" },
                              { value: EventInsuranceType.THEFT, label: "ROUBO/FURTO" },
                              { value: EventInsuranceType.MOTORCYCLE_RETRIEVAL, label: "RECOLHIMENTO DA MOTOCICLETA (R$200,00 ATÉ 50KM + R$2,00 POR KM EXCEDENTE)" },
                              { value: EventInsuranceType.WHEEL_OR_TIRE_DAMAGE, label: "RODA AMASSADA / PNEU CORTADO (R$80,00)" },
                              { value: EventInsuranceType.KEY_REPLACEMENT, label: "SUBSTITUIÇÃO DE CHAVE OU MIOLO (R$300,00)" },
                              { value: EventInsuranceType.WITHDRAWAL_CANCELLATION, label: "DESISTÊNCIA DE RETIRADA (R$100,00)" },
                              { value: EventInsuranceType.OUT_OF_COVERAGE_RADIUS, label: "EXCEDER O RAIO DE ATUAÇÃO (R$150,00)" },
                              { value: EventInsuranceType.MAINTENANCE_WITHOUT_SCHEDULING, label: "MANUTENÇÃO SEM AGENDAMENTO (R$50,00)" },
                              { value: EventInsuranceType.NO_SHOW, label: "NO SHOW - AUSÊNCIA / ATRASO EM AGENDAMENTO (R$25,00)" },
                              { value: EventInsuranceType.MOTORCYCLE_SEIZURE, label: "APREENSÃO DA MOTOCICLETA (R$700,00)" },
                              { value: EventInsuranceType.BLOCKED_FOR_NONPAYMENT, label: "BLOQUEIO POR INADIMPLÊNCIA, MAU USO OU EXCEDER A VELOCIDADE MÁXIMA DE CONDUÇÃO (R$20,00)" },
                              { value: EventInsuranceType.MISCONDUCT_OR_ABUSE, label: "CONSTATAÇÃO DE MAU USO OU CONDUTA (R$300,00)" }
                            ]}
                            required={true}
                          />
                        </div>

                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Data e hora do evento:"
                            name="eventDate"
                            type="datetime-local"
                            className="w-full"
                            required={true}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Local do evento:"
                            name="location"
                            className="w-full"
                          />
                        </div>

                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Estado:"
                            name="state"
                            className="w-full"
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Cidade:"
                            name="city"
                            className="w-full"
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Placa:"
                            name="licensePlate"
                            className="w-full"
                            required={true}
                            mask={maskLicensePlate}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">


                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Nome do associado:"
                            name="associateName"
                            className="w-full"
                            required={true}
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <ComboBoxFormik
                            label="Tipo do veículo:"
                            name="vehicleType"
                            options={[
                              { value: "", label: "-" },
                              { value: VehicleInsuranceType.CAR, label: "Carro" },
                              { value: VehicleInsuranceType.MOTORCYCLE, label: "Moto" },
                              { value: VehicleInsuranceType.TRUCK, label: "Caminhão" }
                            ]}
                            required={true}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">


                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Telefone de WhatsApp:"
                            name="whatsapp"
                            type="tel"
                            className="w-full"
                            mask={maskPhone}
                            required={true}
                          />
                        </div>
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="Telefone:"
                            name="phone"
                            type="tel"
                            className="w-full"
                            mask={maskPhone}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row flex-wrap">

                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <InputFormik
                            label="E-mail:"
                            name="email"
                            type="email"
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap mt-2 mb-2">
                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <div className="mb-2 block">
                            <label htmlFor="associatedTrigger" className="block text-sm font-medium">
                              Acionamento Associado?
                            </label>
                          </div>
                          <div className="flex gap-4">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="associatedTriggerTrue"
                                name="associatedTrigger"
                                className="mr-2"
                                onChange={() => setFieldValue("associatedTrigger", true)}
                                checked={values.associatedTrigger === true}
                              />
                              <label htmlFor="associatedTriggerTrue">Sim</label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="associatedTriggerFalse"
                                name="associatedTrigger"
                                className="mr-2"
                                onChange={() => setFieldValue("associatedTrigger", false)}
                                checked={values.associatedTrigger === false}
                              />
                              <label htmlFor="associatedTriggerFalse">Não</label>
                            </div>
                          </div>
                        </div>

                        <div className="w-full md:w-1/2 md:pr-2 mb-4 md:mb-0">
                          <div className="mb-2 block">
                            <label htmlFor="thirdPartiesInvolved" className="block text-sm font-medium">
                              Houve terceiros?
                            </label>
                          </div>
                          <div className="flex gap-4">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="thirdPartiesInvolvedTrue"
                                name="thirdPartiesInvolved"
                                className="mr-2"
                                onChange={() => setFieldValue("thirdPartiesInvolved", true)}
                                checked={values.thirdPartiesInvolved === true}
                              />
                              <label htmlFor="thirdPartiesInvolvedTrue">Sim</label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="thirdPartiesInvolvedFalse"
                                name="thirdPartiesInvolved"
                                className="mr-2"
                                onChange={() => setFieldValue("thirdPartiesInvolved", false)}
                                checked={values.thirdPartiesInvolved === false}
                              />
                              <label htmlFor="thirdPartiesInvolvedFalse">Não</label>
                            </div>
                          </div>
                        </div>

                        <div className="w-full pl-2 mt-5">
                          {values.thirdPartiesInvolved && (
                            <div className="mb-4">
                              <label htmlFor="thirdPartiesCount" className="block text-sm font-medium">
                                Número de terceiros envolvidos:
                              </label>
                              <div className="flex items-center mt-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentCount = values.thirdPartiesCount || 0;
                                    if (currentCount > 0) {
                                      setFieldValue("thirdPartiesCount", currentCount - 1);
                                    }
                                  }}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded-l"
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  id="thirdPartiesCount"
                                  name="thirdPartiesCount"
                                  value={values.thirdPartiesCount || 0}
                                  onChange={(e) => {
                                    const count = parseInt(e.target.value, 10) || 0;
                                    setFieldValue("thirdPartiesCount", Math.max(0, count));
                                  }}
                                  className="w-16 text-center border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                  min="0"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentCount = values.thirdPartiesCount || 0;
                                    setFieldValue("thirdPartiesCount", currentCount + 1);
                                  }}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded-r"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="w-full pl-2 mt-3">
                          {values.thirdPartiesInvolved && values.thirdPartiesCount > 0 && (
                            <div className="border rounded-lg p-3 bg-gray-50 mb-4">
                              <h3 className="font-medium text-lg mb-3">Dados dos terceiros envolvidos</h3>

                              {Array.from({ length: values.thirdPartiesCount }).map((_, index) => (
                                <div key={index} className="mb-4 p-3 border rounded bg-white">
                                  {/* Cabeçalho com o título e botão de exclusão */}
                                  <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-medium text-md">Terceiro #{index + 1}</h4>

                                    {/* Botão de exclusão */}
                                    <button
                                      type="button"
                                      className="text-red-500 hover:text-red-700"
                                      onClick={() => {
                                        // 1. Remove este terceiro do array
                                        if (values.thirdParties && values.thirdParties.length > index) {
                                          const updatedThirdParties = [...values.thirdParties];
                                          updatedThirdParties.splice(index, 1);
                                          setFieldValue('thirdParties', updatedThirdParties);
                                        }

                                        // 2. Decrementa o contador de terceiros
                                        setFieldValue('thirdPartiesCount', Math.max(0, values.thirdPartiesCount - 1));
                                      }}
                                      aria-label="Excluir terceiro"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  </div>

                                  {/* Resto do conteúdo permanece igual */}
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    <div className="flex-1">
                                      <InputFormik
                                        label="Nome:"
                                        name={`thirdParties[${index}].name`}
                                        className="w-full"
                                      />
                                    </div>
                                    <div className="w-1/3">
                                      <InputFormik
                                        label="Placa:"
                                        name={`thirdParties[${index}].licensePlate`}
                                        type="tel"
                                        className="w-full"
                                        mask={maskLicensePlate}
                                      />
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap gap-2">
                                    <div key={`third-party-${index}-${values.thirdParties && values.thirdParties[index]?.eventThirdPartyType || index}`} className="flex-1">
                                      <ComboBoxFormik
                                        label="Tipo do evento:"
                                        name={`thirdParties[${index}].eventThirdPartyType`}
                                        value={values.thirdParties && values.thirdParties[index]?.eventThirdPartyType || ''}
                                        options={[
                                          { value: "", label: "-" },
                                          { value: EventInsuranceType.REVIEW, label: "REVISÃO" },
                                          { value: EventInsuranceType.COLLISION, label: "COLISÃO" },
                                          { value: EventInsuranceType.THEFT, label: "ROUBO/FURTO" },
                                          { value: EventInsuranceType.MOTORCYCLE_RETRIEVAL, label: "RECOLHIMENTO DA MOTOCICLETA (R$200,00 ATÉ 50KM + R$2,00 POR KM EXCEDENTE)" },
                                          { value: EventInsuranceType.WHEEL_OR_TIRE_DAMAGE, label: "RODA AMASSADA / PNEU CORTADO (R$80,00)" },
                                          { value: EventInsuranceType.KEY_REPLACEMENT, label: "SUBSTITUIÇÃO DE CHAVE OU MIOLO (R$300,00)" },
                                          { value: EventInsuranceType.WITHDRAWAL_CANCELLATION, label: "DESISTÊNCIA DE RETIRADA (R$100,00)" },
                                          { value: EventInsuranceType.OUT_OF_COVERAGE_RADIUS, label: "EXCEDER O RAIO DE ATUAÇÃO (R$150,00)" },
                                          { value: EventInsuranceType.MAINTENANCE_WITHOUT_SCHEDULING, label: "MANUTENÇÃO SEM AGENDAMENTO (R$50,00)" },
                                          { value: EventInsuranceType.NO_SHOW, label: "NO SHOW - AUSÊNCIA / ATRASO EM AGENDAMENTO (R$25,00)" },
                                          { value: EventInsuranceType.MOTORCYCLE_SEIZURE, label: "APREENSÃO DA MOTOCICLETA (R$700,00)" },
                                          { value: EventInsuranceType.BLOCKED_FOR_NONPAYMENT, label: "BLOQUEIO POR INADIMPLÊNCIA, MAU USO OU EXCEDER A VELOCIDADE MÁXIMA DE CONDUÇÃO (R$20,00)" },
                                          { value: EventInsuranceType.MISCONDUCT_OR_ABUSE, label: "CONSTATAÇÃO DE MAU USO OU CONDUTA (R$300,00)" }
                                        ]}
                                      />
                                    </div>
                                    <div className="w-1/3">
                                      <InputFormik
                                        label="Telefone:"
                                        name={`thirdParties[${index}].phone`}
                                        className="w-full"
                                        mask={maskPhone}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                      </div>

                      <div className="flex flex-wrap mb-4 mt-1">
                        <div className="w-full">
                          <label htmlFor="reportText" className="block mb-2 text-sm font-medium">
                            Relato detalhado do ocorrido:
                          </label>
                          <TextareaFormik
                            label="" // O label já está sendo exibido manualmente acima
                            name="reportText"
                            className="w-full min-h-[150px] rounded shadow-md focus:border-white p-2 border border-gray-300"
                            placeholder="Descreva com detalhes como ocorreu o sinistro, incluindo circunstâncias, danos observados e outras informações relevantes..."
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'documentacao' && (
                    <div className="documentacao">
                      <section className="mt-5 mb-4 p-3">
                        <div className="flex flex-col items-stretch">
                          {(values.documents || []).map((doc, index) => (
                            <div key={index} className={`flex flex-col space-y-2 p-3 my-0 rounded ${index % 2 ? 'bg-gray/5' : 'bg-gray/25'}`}>
                              <div className="flex justify-between items-start">
                                <div className="flex-grow">
                                  <DocumentEventClaimRow name={`documents.${index}`} initialData={doc || {}} />
                                </div>
                                <button
                                  type="button"
                                  className="text-red-500 hover:text-red-700 p-2"
                                  onClick={() => {
                                    // Cria uma cópia do array sem o documento que queremos excluir
                                    const updatedDocuments = [...values.documents];
                                    updatedDocuments.splice(index, 1);

                                    // Atualiza o campo documents com o novo array
                                    setFieldValue('documents', updatedDocuments);
                                  }}
                                  aria-label="Excluir documento"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="button-adicionar-documento mt-3 bg-gray text-white py-2 px-4 rounded"
                            onClick={() => {
                              setFieldValue(
                                `documents.${values.documents?.length || 0}`,
                                { type: '', content: '', verified: false }
                              );
                            }}
                          >
                            + Adicionar documento
                          </button>
                        </div>
                      </section>
                    </div>
                  )}

                  {/* Botões do formulário */}
                  <div className="flex justify-end space-x-3 pt-4">

                    <ButtonWithLoader
                      type="submit"
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      Salvar
                    </ButtonWithLoader>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>


      </Modal>
    </Suspense>

  );
}